import { DatePipe, NgIf, NgStyle, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { delay, take } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { NoValue } from '@shared/constants/empty-value.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { AvatarModule } from '@ui-components/components/avatar/avatar.module';
import { IconComponent } from '@ui-components/components/icon/icon.component';

import { CustomFieldsService, PropertyCustomFieldStateModel } from '../../services/custom-fields.service';

export interface CustomFieldHistoryModalData {
  title: string;
  contextId: number;
  customFieldId: number;
  users: RestUserModel[];
  dropDownOptions: {
    label: string;
    value?: string;
    color?: string;
    groupLabel?: string;
  }[];
}

@UntilDestroy()
@Component({
  selector: 'app-custom-field-history-modal',
  standalone: true,
  templateUrl: './custom-field-history-modal.component.html',
  styleUrls: ['./custom-field-history-modal.component.scss'],
  imports: [
    NgIf,
    NgStyle,
    MatButtonModule,
    MatDialogModule,
    MatTableModule,
    DatePipe,
    MatSortModule,
    AvatarModule,
    IconComponent,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldHistoryModalComponent extends PuSubscribable implements OnInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;

  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(MatDialogRef<CustomFieldHistoryModalComponent>);
  public data = inject<CustomFieldHistoryModalData>(MAT_DIALOG_DATA);
  private customFieldsService = inject(CustomFieldsService);

  isLoading = true;
  dataSource: MatTableDataSource<PropertyCustomFieldStateModel>;
  displayedColumns: string[] = ['modified', 'value', 'deleted'];

  private get flatDropDownOptions() {
    return this.data.dropDownOptions?.flat?.() ?? [];
  }

  ngOnInit(): void {
    this.customFieldsService
      .getCustomFieldHistory(this.data.contextId, this.data.customFieldId)
      .pipe(delay(300), take(1))
      .subscribe(history => {
        this.dataSource = new MatTableDataSource(history);
        this.isLoading = false;
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  isLabeledObject(value: any): value is { label: string; color?: string } {
    return typeof value === 'object' && value !== null && 'label' in value;
  }

  getValue(row: PropertyCustomFieldStateModel): { label: string | number; groupLabel?: string; color?: string } {
    if (row.boolValue === true || row.boolValue === false)
      return row.boolValue === true ? { label: 'Yes' } : { label: 'No' };
    if (row.userIdValue) return { label: this.data.users.find(u => u.id === row.userIdValue)?.displayName ?? NoValue };
    if (row.moneyValue) return { label: this.customFieldsService.formatAmount(row.moneyValue) ?? NoValue };
    if (row.dateValue) return { label: formatDate(row.dateValue, 'MM/dd/yy', 'en-US') ?? NoValue };
    if (row.dateTimeValue) return { label: formatDate(row.dateTimeValue, 'MM/dd/yy hh:mm a', 'en-US') ?? NoValue };
    if (row.dropDownValue) {
      const match = this.flatDropDownOptions.find(option => option.value === row.dropDownValue);
      return match ? { label: match.label, groupLabel: match.groupLabel, color: match.color } : { label: NoValue };
    }

    return { label: row.textValue ?? row.intValue ?? row.numberValue ?? NoValue };
  }

  getUser(userId: number): RestUserModel {
    return this.data.users.find(u => u.id === userId);
  }

  close() {
    this.dialogRef.close();
  }
}
